/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { useEffect, useReducer } from "react";
import clsx from "clsx";

import { JobsFilters, JobsTable } from "@/components";

import { defaultJobsSortKeyAndOrder } from "@/lib/constants";
import { JobsResponse, JobsSortKeyAndOrder, JobStatus } from "@/types";

type FilterAndSortState = {
  title: string;
  sortKeyAndOrder: JobsSortKeyAndOrder;
  cities: string[];
  statuses: JobStatus[];
};

type State = {
  allJobs: JobsResponse[];
  filteredAndSortedJobs: JobsResponse[];
  filterAndSortState: FilterAndSortState;
};

type Action =
  | { type: "title"; payload: string }
  // prettier-ignore
  | { type: "sortOrderAndKey"; payload: JobsSortKeyAndOrder }
  | { type: "cities"; payload: string[] }
  | { type: "statuses"; payload: JobStatus[] }
  | { type: "jobs"; payload: JobsResponse[] };

const filterAndSortJobs = ({
  filterAndSortState,
  jobs,
}: {
  filterAndSortState: FilterAndSortState;
  jobs: JobsResponse[];
}) => {
  let filteredAndSortedJobs = [...jobs];

  // 1. Filter by city.
  const citySet = new Set(filterAndSortState.cities);
  filteredAndSortedJobs = filteredAndSortedJobs.filter(
    ({ job }) =>
      // If the city set is empty, then we are in a default
      // state and we want to show jobs across all cities.
      (citySet.size === 0 && job.city !== "") || citySet.has(job.city),
  );

  // 2. Filter by job status.
  const statusesSet = new Set(filterAndSortState.statuses);
  filteredAndSortedJobs = filteredAndSortedJobs.filter(
    ({ job }) =>
      // If the statuses set is empty, then we are in a default
      // state and we only want show jobs that are not closed.
      (statusesSet.size === 0 && job.status !== JobStatus.CLOSED) ||
      statusesSet.has(job.status),
  );

  // 3. Filter by job title.
  filteredAndSortedJobs = filteredAndSortedJobs.filter(({ job }) =>
    job.title.toLowerCase().includes(filterAndSortState.title.toLowerCase()),
  );

  // 4. Sort the jobs.
  filteredAndSortedJobs.sort(({ job: jobA }, { job: jobB }) => {
    switch (filterAndSortState.sortKeyAndOrder) {
      case JobsSortKeyAndOrder.CreatedAtAsc:
        // prettier-ignore
        return new Date(jobA.createdAt).getTime() - new Date(jobB.createdAt).getTime()
      case JobsSortKeyAndOrder.CreatedAtDesc:
        // prettier-ignore
        return new Date(jobB.createdAt).getTime() - new Date(jobA.createdAt).getTime()
      case JobsSortKeyAndOrder.TitleAsc:
        return jobA.title.localeCompare(jobB.title);
      case JobsSortKeyAndOrder.TitleDesc:
        return jobB.title.localeCompare(jobA.title);
      default:
        return 0;
    }
  });

  return filteredAndSortedJobs;
};

const reducer = (state: State, action: Action): State => {
  // If new jobs came in, then re-filter and re-sort based on the new jobs.
  if (action.type === "jobs") {
    return {
      ...state,
      allJobs: action.payload,
      filteredAndSortedJobs: filterAndSortJobs({
        filterAndSortState: state.filterAndSortState,
        jobs: action.payload,
      }),
    };
  }

  // Otherwise, we are updating the filter and sort state.
  let newFilterAndSortState: FilterAndSortState = {
    ...state.filterAndSortState,
  };
  switch (action.type) {
    case "title":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        title: action.payload,
      };
      break;
    case "sortOrderAndKey":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        sortKeyAndOrder: action.payload,
      };
      break;
    case "cities":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        cities: action.payload,
      };
      break;
    case "statuses":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        statuses: action.payload,
      };
      break;
    default:
      break;
  }

  // Set the new filter and sort state as well and filter the jobs based on it.
  return {
    ...state,
    filteredAndSortedJobs: filterAndSortJobs({
      filterAndSortState: newFilterAndSortState,
      jobs: state.allJobs,
    }),
    filterAndSortState: newFilterAndSortState,
  };
};

export const JobsFiltersAndTable = ({
  className,
  jobs,
}: {
  className?: string;
  jobs: JobsResponse[];
}) => {
  const allCities = Array.from(new Set(jobs.map(({ job }) => job.city)));
  const [state, dispatch] = useReducer(reducer, {
    allJobs: jobs,
    filteredAndSortedJobs: jobs,
    filterAndSortState: {
      sortKeyAndOrder: defaultJobsSortKeyAndOrder,
      title: "",
      cities: [],
      statuses: [],
    },
  });

  useEffect(() => {
    // ! TODO: Need to make sure that all cities wouldn't change...
    // If new jobs come in (as the result of a duplication or status change action), then update the state.
    dispatch({ type: "jobs", payload: jobs });
  }, [jobs]);

  const setCities = (cities: string[]) => {
    dispatch({ type: "cities", payload: cities });
  };

  const setSortOrderAndKey = (sortOrderAndKey: JobsSortKeyAndOrder) => {
    dispatch({ type: "sortOrderAndKey", payload: sortOrderAndKey });
  };

  const setStatuses = (statuses: JobStatus[]) => {
    dispatch({ type: "statuses", payload: statuses });
  };

  const setTitle = (title: string) => {
    dispatch({ type: "title", payload: title });
  };

  return (
    <div className={clsx("flex flex-col gap-4", className)}>
      <JobsFilters
        allCities={allCities}
        cities={state.filterAndSortState.cities}
        setCities={setCities}
        setSortOrderAndKey={setSortOrderAndKey}
        setStatuses={setStatuses}
        setTitle={setTitle}
        sortKeyAndOrder={state.filterAndSortState.sortKeyAndOrder}
        statuses={state.filterAndSortState.statuses}
        title={state.filterAndSortState.title}
      />
      <JobsTable jobs={state.filteredAndSortedJobs} />
    </div>
  );
};
